import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import Latex from 'react-latex-next';
import { IoChevronBack, IoChevronForward } from 'react-icons/io5';
import { BASELINE } from '../../../../util';

function ReviewQuizContent({ quizData, handleNext }) {
    const [htmlContent, setHtmlContent] = useState('');
    const [questions, setQuestions] = useState([]);
    const [loading, setLoading] = useState(true);

    const [currentIndex, setCurrentIndex] = useState(0);
    const [selectedAnswers, setSelectedAnswers] = useState({});
    const [showExplanation, setShowExplanation] = useState(false);
    const [lockedAnswers, setLockedAnswers] = useState({});

    const [isQuizFinished, setIsQuizFinished] = useState(false);
    const [passStatus, setPassStatus] = useState(false); // Whether the user passes or not

    useEffect(() => {
        if (!quizData?.html) return;

        const fetchHTML = async () => {
            try {
                const url = `Class_Materials/${quizData.html}`;
                const res = await axios.get(BASELINE + url, { responseType: 'text' });
                setHtmlContent(res.data);
            } catch (error) {
                console.error('Error fetching HTML:', error);
                setLoading(false);
            }
        };

        fetchHTML();
    }, [quizData?.html]);

    // 2) Once htmlContent is loaded, request AI to generate questions
    useEffect(() => {
        if (!htmlContent) return;

        const systemMessage = {
            role: 'system',
            content: 'You are an AI that creates quiz questions based on user-provided study material.'
        };

        const questionCount = quizData.questionsCount || 5;

        // The user message instructing the AI to create quiz questions from the HTML content
        const userMessage = {
            role: 'user',
            content: `
      You are given some HTML study material. 
      
      Your task is to generate exactly ${questionCount} multiple-choice questions based on that material. 
      Each question must have these seven fields:
      {
        "Question": "Main question text (minimum 10 characters)",
        "optionA": "Option A text",
        "optionB": "Option B text",
        "optionC": "Option C text",
        "optionD": "Option D text",
        "Answer": "Correct answer: must be 'A', 'B', 'C', or 'D'. Distribute these answers randomly so they are not all the same.",
        "rationale": "Explanation (minimum 10 characters)"
      }
      
      Important:
      1. You must choose the correct answer randomly among 'A', 'B', 'C', and 'D' for each question. 
         Do not always pick the same letter. 
      2. Return your response strictly as a JSON array with ${questionCount} objects. 
         No additional text, explanations, or code formatting outside the JSON array. 
      3. Your answer must start with '[' and end with ']'.
      
      (Study material Question must contain the STUDY MATERIAL. MUST CONTAINED):
      ${htmlContent}
      `
        };

        const fetchQuestionsFromAI = async () => {
            setLoading(true);
            try {
                const payload = { messages: [systemMessage, userMessage] };
                const res = await axios.post(`${BASELINE}askai4`, payload);

                if (res.data && res.data.message) {
                    let rawText = res.data.message.trim();

                    // 1) Normalize the first bracket
                    let firstBracketIndex = rawText.indexOf('[');
                    if (firstBracketIndex === -1) {
                        // If there's no "[" at all, just prepend it
                        rawText = '[' + rawText;
                    } else {
                        // Keep everything from the first bracket onward
                        rawText = rawText.substring(firstBracketIndex);
                    }

                    // 2) Normalize the last bracket
                    let lastBracketIndex = rawText.lastIndexOf(']');
                    if (lastBracketIndex === -1) {
                        // If there's no "]" at all, just append it
                        rawText = rawText + ']';
                    } else {
                        // Keep everything up to (and including) the last bracket
                        rawText = rawText.substring(0, lastBracketIndex + 1);
                    }

                    // Now attempt to parse
                    let parsed = JSON.parse(rawText);

                    if (!Array.isArray(parsed)) {
                        throw new Error('AI response is not an array.');
                    }
                    setQuestions(parsed);
                }
            } catch (err) {
                console.error('Error requesting quiz from AI:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchQuestionsFromAI();
    }, [htmlContent, quizData.questionsCount]);

    // Select an answer
    const handleSelectAnswer = (answer) => {
        if (lockedAnswers[currentIndex] || showExplanation) return;
        setSelectedAnswers((prev) => ({
            ...prev,
            [currentIndex]: answer
        }));
    };

    // Move to the next question or check the current one
    const handleNextQuestion = () => {
        if (!showExplanation && selectedAnswers[currentIndex]) {
            // If the user has selected an answer and we haven't shown the explanation yet, "Check"
            setLockedAnswers((prev) => ({ ...prev, [currentIndex]: true }));
            setShowExplanation(true);
        } else if (showExplanation) {
            // If the explanation is shown, move on to the next
            if (currentIndex < questions.length - 1) {
                const nextIndex = currentIndex + 1;
                setCurrentIndex(nextIndex);
                setShowExplanation(!!lockedAnswers[nextIndex]);
            } else {
                // If we're on the last question, calculate the score
                calculateScoreAndFinish();
            }
        }
    };

    // Go to the previous question
    const handlePrevQuestion = () => {
        if (currentIndex > 0) {
            const prevIndex = currentIndex - 1;
            setCurrentIndex(prevIndex);
            setShowExplanation(!!lockedAnswers[prevIndex]);
        }
    };

    // Calculate the final score, comparing user selections to q.Answer
    const calculateScoreAndFinish = () => {
        let correctCount = 0;
        questions.forEach((q, idx) => {
            const userAnswer = selectedAnswers[idx];
            // The AI-provided correct answer is in q.Answer: e.g., "A", "B", "C", or "D"
            if (userAnswer && userAnswer.toUpperCase() === q.Answer.toUpperCase()) {
                correctCount++;
            }
        });

        const passNeeded = quizData.pass || 3;
        setPassStatus(correctCount >= passNeeded);
        setIsQuizFinished(true);
    };

    const handleRetry = () => {
        // Reset for a retry
        setSelectedAnswers({});
        setShowExplanation(false);
        setLockedAnswers({});
        setCurrentIndex(0);
        setPassStatus(false);
        setIsQuizFinished(false);
    };

    // If quiz is finished and the user passes, proceed
    useEffect(() => {
        if (isQuizFinished) {
            if (passStatus) {
                // If passed, call handleNext or onComplete
                if (handleNext) {
                    handleNext();
                }
            }
            // Otherwise, user will see the "retry" option
        }
    }, [isQuizFinished, passStatus, handleNext]);

    // Loading indicator
    if (loading) {
        return (
            <div className="w-full h-full flex items-center justify-center">
                <div className="text-gray-300">Generating questions...</div>
            </div>
        );
    }

    // If no questions were generated
    if (!questions.length) {
        return (
            <div className="w-full h-full flex items-center justify-center">
                <p className="text-gray-400">No questions were generated.</p>
            </div>
        );
    }

    // If quiz is finished but user didn't pass
    if (isQuizFinished && !passStatus) {
        return (
            <div className="flex flex-col items-center justify-center p-6 bg-[#2b2b2b] rounded-md">
                <h2 className="text-white text-xl font-bold mb-4">Try Again</h2>
                <p className="text-gray-300 mb-6">
                    You did not reach the passing score. Would you like to try again?
                </p>
                <button
                    onClick={handleRetry}
                    className="px-4 py-2 bg-[#5c8dff] rounded text-white"
                >
                    Retry
                </button>
            </div>
        );
    }

    // If quiz is in progress
    const currentQ = questions[currentIndex];
    const userAnswer = selectedAnswers[currentIndex];
    const isExplanationVisible = showExplanation && lockedAnswers[currentIndex];

    // We'll determine the correct answer's option key. For example,
    // if q.Answer === "C", then correctOptionKey = "optionC".
    const correctOptionKey = 'option' + (currentQ.Answer || '').toUpperCase();

    return (
        <div className="flex flex-col h-full">
            {/* Progress info */}
            <div className="flex justify-between mb-4 text-sm text-gray-400">
                <div>
                    Question {currentIndex + 1} / {questions.length}
                </div>
                <div>
                    Answered {Object.keys(selectedAnswers).length} / {questions.length}
                </div>
            </div>

            {/* Question and choices */}
            <div className="flex-1 flex flex-col md:flex-row gap-4">
                {/* Question area */}
                <div className="flex-1 bg-[#252525] p-4 rounded">
                    <h3 className="text-white mb-3">
                        <Latex>{currentQ.Question}</Latex>
                    </h3>
                    {isExplanationVisible && (
                        <div className="mt-6 pt-4 border-t border-gray-600">
                            <h4 className="text-[#5c8dff] font-semibold">Explanation</h4>
                            <p className="text-gray-200 mt-2">
                                <Latex>{currentQ.rationale}</Latex>
                            </p>
                        </div>
                    )}
                </div>

                {/* Options area */}
                <div className="w-full md:w-[300px] flex flex-col space-y-3">
                    {['optionA', 'optionB', 'optionC', 'optionD'].map((opt) => {
                        // The user’s chosen letter is userAnswer ("A", "B", "C", or "D").
                        // The current option is "optionA" etc.
                        // So isSelected is whether userAnswer matches opt.replace('option','').
                        const isSelected = userAnswer === opt.replace('option', '');

                        // Now we compare with correctOptionKey to highlight the correct answer
                        const isCorrect = isExplanationVisible && opt.toLowerCase() === correctOptionKey.toLowerCase();
                        const isUserWrong = isExplanationVisible && isSelected && opt.toLowerCase() !== correctOptionKey.toLowerCase();

                        return (
                            <button
                                key={opt}
                                onClick={() => handleSelectAnswer(opt.replace('option', ''))}
                                disabled={lockedAnswers[currentIndex] || showExplanation}
                                className={`
                  p-3 rounded text-left
                  ${isExplanationVisible
                                        ? isCorrect
                                            ? 'bg-green-600 text-white'
                                            : isUserWrong
                                                ? 'bg-red-600 text-white'
                                                : 'bg-[#252525] text-gray-300'
                                        : isSelected
                                            ? 'bg-[#5c8dff] text-white'
                                            : 'bg-[#252525] hover:bg-[#303030] text-gray-200'
                                    }
                `}
                            >
                                <Latex>{currentQ[opt]}</Latex>
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Navigation buttons */}
            <div className="mt-4 flex justify-between">
                <button
                    onClick={handlePrevQuestion}
                    disabled={currentIndex === 0}
                    className="px-4 py-2 bg-[#2b2b2b] rounded text-gray-300 disabled:opacity-50 flex items-center gap-1"
                >
                    <IoChevronBack /> Previous
                </button>
                <button
                    onClick={handleNextQuestion}
                    disabled={!userAnswer}
                    className="px-4 py-2 bg-[#5c8dff] text-white rounded flex items-center gap-1 disabled:opacity-50"
                >
                    {showExplanation
                        ? currentIndex === questions.length - 1
                            ? 'Finish'
                            : 'Next'
                        : 'Check'}
                    {!showExplanation && <IoChevronForward />}
                </button>
            </div>
        </div>
    );
}

export default ReviewQuizContent;
